import React, { useEffect, useState, useCallback } from 'react'
import TopBar from '../../components/TopBar'
import { useDispatch, useSelector } from 'react-redux';
import Loading from '../../components/Loading';
import doctorPics from "../../assets/clinic.jpg";
import { checkAppointment, resetCheckAppointment } from '../../redux/actions/checkAppointment';
import BottomNav from '../../components/BottomNav';
import { useNavigate } from 'react-router-dom';
import { FaVideo, FaMicrophone, FaCalendarCheck, FaClock, FaHospital, FaPlay, FaExclamationTriangle, FaCheckCircle, FaSync } from 'react-icons/fa';

export default function CheckAppointmentDay() {
    const store = useSelector(state => state.checkAppointment);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [isRefreshing, setIsRefreshing] = useState(false);
    
    console.log('store store', store);
    
    const handleLive = (id) => {
        navigate('/appointment/live', {state: id})
    }

    const fetchAppointments = useCallback(() => {
        const userInfo = localStorage.getItem("userInfo");
        if (userInfo) {
            const userId = JSON.parse(userInfo).id;
            dispatch(checkAppointment(userId));
        }
    }, [dispatch]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        dispatch(resetCheckAppointment());
        fetchAppointments();
        setTimeout(() => setIsRefreshing(false), 1000);
    };

    useEffect(() => {
        fetchAppointments();
    }, [fetchAppointments]);


    useEffect(() => {
        const handleFocus = () => {
            dispatch(resetCheckAppointment());
            fetchAppointments();
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [fetchAppointments, dispatch]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            dispatch(resetCheckAppointment());
        };
    }, [dispatch]);

    const appointments = store?.data?.appointment || [];

    const getAppointmentIcon = (mode) => {
        return mode === 'audio' ? FaMicrophone : FaVideo;
    };

    const getAppointmentColor = (mode) => {
        return mode === 'audio' ? 'from-primary100 to-emerald-600' : 'from-primary100 to-blue-600';
    };

    return (
        <div className='min-h-screen bg-gradient-to-br from-primary100/10 via-emerald-50 to-blue-50'>
            <TopBar title={"Current Appointment"} url={-1} />

            <div className="px-4 py-6">
                {/* Hero Section */}
                <div className="bg-white rounded-3xl shadow-xl p-6 mb-6 border border-primary100/20">
                    <div className="flex items-center justify-center mb-4">
                        <div className="w-16 h-16 bg-gradient-to-r from-primary100 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                            <FaCalendarCheck className="text-white text-2xl" />
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-800 text-center mb-2">
                                Today's Appointments
                            </h2>
                            <p className="text-gray-600 text-center">
                                Please enter appointment room at least 5 minutes before the scheduled time
                            </p>
                        </div>
                        <button
                            onClick={handleRefresh}
                            disabled={isRefreshing || store?.loading}
                            className="ml-4 p-3 bg-primary100 text-white rounded-full hover:bg-primary100/80 transition-colors disabled:opacity-50"
                            title="Refresh appointments"
                        >
                            <FaSync className={`text-lg ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Loading State */}
                {(store?.loading || isRefreshing) && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loading variant='medical' />
                        <p className="text-gray-600 mt-4">
                            {isRefreshing ? 'Refreshing appointments...' : 'Loading your appointments...'}
                        </p>
                    </div>
                )}

                {/* Appointments List */}
                {!store?.loading && !isRefreshing && (
                    <div className="space-y-4 pb-20">
                        {appointments?.length > 0 ? (
                            appointments.map((item, index) => {
                                const AppointmentIcon = getAppointmentIcon(item.appt_mode);
                                const gradientColor = getAppointmentColor(item.appt_mode);
                                
                                return (
                                    <div key={index} className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 border border-primary100/20">
                                        {/* Header with gradient */}
                                        <div className={`bg-gradient-to-r ${gradientColor} p-6 text-white`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-4">
                                                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                                                        <AppointmentIcon className="text-white text-xl" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-xl font-bold">
                                                            {item.appt_mode === 'audio' ? 'Audio Consultation' : 'Video Consultation'}
                                                        </h3>
                                                        <p className="text-white text-opacity-90 text-sm">
                                                            {item.appt_type} Appointment
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="flex items-center space-x-1 mb-1">
                                                        <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
                                                        <span className="text-sm font-medium">Active</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Appointment Details */}
                                        <div className="p-6">
                                            {/* Clinic Info */}
                                            <div className="flex items-center space-x-4 mb-6">
                                                <div className="w-16 h-16 rounded-2xl overflow-hidden bg-gradient-to-br from-primary100/20 to-emerald-100 p-1">
                                                    <div className="w-full h-full rounded-xl overflow-hidden">
                                                        <img 
                                                            src={doctorPics} 
                                                            alt="Clinic" 
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-lg font-semibold text-gray-800 truncate">
                                                        {item.clinic_name || "Medical Clinic"}
                                                    </h4>
                                                    <div className="flex items-center space-x-2 text-gray-500">
                                                        <FaHospital className="text-sm text-primary100" />
                                                        <span className="text-sm truncate">{JSON.parse(localStorage.getItem("hospital_name")) || "Healthcare Portal"}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Time Details */}
                                            <div className="grid grid-cols-2 gap-4 mb-6">
                                                <div className="bg-primary100/10 rounded-xl p-4 border border-primary100/20">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-10 h-10 bg-primary100/20 rounded-full flex items-center justify-center">
                                                            <FaCalendarCheck className="text-primary100 text-sm" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm text-gray-500">Date</p>
                                                            <p className="font-semibold text-gray-800">{item.appt_date}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                                                            <FaClock className="text-emerald-600 text-sm" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm text-gray-500">Time</p>
                                                            <p className="font-semibold text-gray-800">{item.appt_time}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action Button */}
                                            <button
                                                onClick={() => handleLive(item.id)}
                                                className={`w-full bg-gradient-to-r ${gradientColor} text-white py-4 px-6 rounded-2xl font-semibold text-lg flex items-center justify-center space-x-3 hover:shadow-lg transform hover:scale-105 transition-all duration-200 shadow-md`}
                                            >
                                                <FaPlay className="text-lg" />
                                                <span>Enter {item.appt_mode === 'audio' ? 'Audio' : 'Video'} Room</span>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            // Empty State
                            <div className="bg-white rounded-3xl shadow-xl p-8 text-center border border-primary100/20">
                                <div className="w-24 h-24 bg-primary100/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <FaExclamationTriangle className="text-primary100 text-3xl" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                    No Active Appointments
                                </h3>
                                <p className="text-gray-600 mb-6">
                                    You don't have any active online appointments scheduled for today.
                                </p>
                                <div className="bg-primary100/10 rounded-xl p-4 border border-primary100/20">
                                    <div className="flex items-center justify-center space-x-2 text-primary100">
                                        <FaCheckCircle className="text-sm" />
                                        <span className="text-sm font-medium">Check back when you have scheduled appointments</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <BottomNav color1={'text-primary100'} />
        </div>
    )
}
